import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { BlocksRenderer } from "../components/BlocksRenderer";
import { SedeMapaLink } from "../components/SedeMapaLink";
import { useEdicion } from "../hooks/useCms";
import { mediaUrl } from "../lib/strapi";
import type { AcentoColor, EdicionPrograma } from "../types/cms";
import { NotFoundPage } from "./NotFoundPage";

/**
 * Página de detalle de UNA edición concreta de un programa.
 * URL: /programas/:slug/:edicion
 *   - :slug      → slug del programa (proyecto-verano, feria-de-ciencias, …)
 *   - :edicion   → slug de la edición (ej. "2026", "verano-2026", …)
 *
 * Esta página depende del CMS: si la edición no está creada y publicada
 * en el admin, devolvemos NotFound. No hay fallback estático porque las
 * ediciones son contenido editorial puro (fechas, sedes, monitores, …)
 * que cambia cada año.
 */

const accentTextDeep: Record<AcentoColor, string> = {
  leaf: "text-accent-leaf-deep",
  cobalt: "text-accent-cobalt-deep",
  sun: "text-accent-sun-deep",
  violet: "text-accent-violet-deep",
  magenta: "text-accent-magenta-deep",
};

const accentBg: Record<AcentoColor, string> = {
  leaf: "bg-accent-leaf",
  cobalt: "bg-accent-cobalt",
  sun: "bg-accent-sun",
  violet: "bg-accent-violet",
  magenta: "bg-accent-magenta",
};

const estadoLabel: Record<EdicionPrograma["estado"], string> = {
  proximamente: "Próximamente",
  abierta: "Inscripción abierta",
  cerrada: "Inscripción cerrada",
  finalizada: "Finalizada",
};

// Estilos por estado. Mantengo solo clases de tailwind.config.js — sin
// "soft" ni grises intermedios que no existan en el tema.
const estadoStyle: Record<EdicionPrograma["estado"], string> = {
  proximamente: "bg-gray-100 text-gray-700 ring-gray-300",
  abierta: "bg-accent-leaf text-brand-paper ring-accent-leaf-deep",
  cerrada: "bg-gray-100 text-gray-700 ring-gray-300",
  finalizada: "bg-brand-ink text-brand-paper ring-brand-ink",
};

function formatRangoFechas(inicio?: string, fin?: string): string | null {
  if (!inicio && !fin) return null;
  const fmt = (d?: string) =>
    d
      ? new Date(d).toLocaleDateString("es-ES", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : null;
  const a = fmt(inicio);
  const b = fmt(fin);
  if (a && b) return `Del ${a} al ${b}`;
  return a ?? b;
}

export function EdicionDetallePage() {
  const { slug: programaSlug, edicion: edicionSlug } = useParams<{
    slug: string;
    edicion: string;
  }>();
  const { data: edicion, isLoading } = useEdicion(edicionSlug ?? "");

  if (!programaSlug || !edicionSlug) return <NotFoundPage />;

  if (isLoading) {
    return (
      <section className="container-emjc py-20">
        <p className="text-gray-700">Cargando…</p>
      </section>
    );
  }

  if (!edicion) return <NotFoundPage />;

  // Si la edición existe pero no pertenece al programa de la URL, también 404:
  // evita URLs cruzadas tipo /programas/feria/2026 cuando 2026 es de Proyecto Verano.
  if (edicion.programa && edicion.programa.slug !== programaSlug) {
    return <NotFoundPage />;
  }

  const programa = edicion.programa;
  const accent: AcentoColor = (programa?.acentoColor as AcentoColor | undefined) ?? "leaf";
  const nombrePrograma = programa?.nombre ?? "Programa";
  const fechas = formatRangoFechas(edicion.fechaInicio, edicion.fechaFin);
  // Si el equipo ha rellenado el campo `titulo`, lo usamos como h1 y mostramos
  // "Edición {año}" como badge sobre la cabecera. Si no hay titulo, mantenemos
  // el comportamiento histórico ("Edición {año}" como h1).
  const headlineH1 = edicion.titulo?.trim() || `Edición ${edicion.anio}`;
  const showAnioBadge = Boolean(edicion.titulo?.trim());
  const seoTitle = edicion.titulo
    ? `${edicion.titulo} (${edicion.anio}) — ${nombrePrograma}`
    : `${nombrePrograma} ${edicion.anio}`;
  const seoDescription =
    edicion.descripcion?.[0]?.type === "paragraph"
      ? (edicion.descripcion[0].children[0]?.text ?? `${nombrePrograma} — edición ${edicion.anio}`).slice(0, 160)
      : `${nombrePrograma} — edición ${edicion.anio}`;

  const matriculaUrl = mediaUrl(edicion.documentoMatricula?.url);

  return (
    <>
      <Helmet>
        <title>{seoTitle} — Campus EMJC</title>
        <meta name="description" content={seoDescription} />
      </Helmet>

      <article className="py-20">
        <div className="container-emjc">
          {/* Migas de pan */}
          <nav aria-label="Migas de pan" className="text-sm text-gray-700">
            <Link to="/programas" className="hover:text-brand-orange-deep hover:underline">
              Programas
            </Link>
            <span aria-hidden="true" className="mx-2">/</span>
            <Link
              to={`/programas/${programaSlug}`}
              className="hover:text-brand-orange-deep hover:underline"
            >
              {nombrePrograma}
            </Link>
            <span aria-hidden="true" className="mx-2">/</span>
            <span aria-current="page">Edición {edicion.anio}</span>
          </nav>

          {/* Cabecera */}
          <header className="mt-6">
            <p
              className={`text-sm font-semibold uppercase tracking-widest ${accentTextDeep[accent]}`}
            >
              {nombrePrograma}
              {showAnioBadge && (
                <span className="ml-2 text-gray-700"> · Edición {edicion.anio}</span>
              )}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-4">
              <h1 className="font-display text-5xl font-semibold leading-tight md:text-6xl">
                {headlineH1}
              </h1>
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-widest ring-1 ${estadoStyle[edicion.estado]}`}
              >
                {estadoLabel[edicion.estado]}
              </span>
            </div>
            {fechas && (
              <p className="mt-4 text-lg text-gray-700">{fechas}</p>
            )}
          </header>

          {/* Imagen de portada de la edición (opcional) */}
          {edicion.imagenPortada?.url && (
            <figure className="mt-10 overflow-hidden rounded-3xl border border-gray-300 bg-white">
              <img
                src={mediaUrl(edicion.imagenPortada.url)}
                alt={edicion.imagenPortada.alternativeText ?? `${nombrePrograma} ${edicion.anio}`}
                className="aspect-[16/9] w-full object-cover"
                loading="lazy"
              />
            </figure>
          )}

          {/* CTA principal: matrícula */}
          {matriculaUrl && edicion.estado === "abierta" && (
            <div className="mt-8">
              <a
                href={matriculaUrl}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-brand-paper shadow-md transition hover:opacity-90 ${accentBg[accent]}`}
              >
                Descargar hoja de matrícula
                <span aria-hidden="true">↓</span>
              </a>
            </div>
          )}
          {matriculaUrl && edicion.estado !== "abierta" && (
            <div className="mt-8">
              <a
                href={matriculaUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-gray-400 px-6 py-3 text-sm font-semibold text-brand-ink transition hover:border-brand-orange hover:text-brand-orange-deep"
              >
                Hoja de matrícula (referencia)
                <span aria-hidden="true">↓</span>
              </a>
            </div>
          )}

          {/* Descripción larga */}
          {edicion.descripcion && edicion.descripcion.length > 0 && (
            <section className="mt-12">
              <BlocksRenderer blocks={edicion.descripcion} />
            </section>
          )}
        </div>

        {/* Sedes participantes */}
        {edicion.sedes && edicion.sedes.length > 0 && (
          <section className="container-emjc mt-16">
            <h2 className="font-display text-3xl font-semibold md:text-4xl">
              {edicion.sedes.length === 1 ? "Sede" : "Sedes participantes"}
            </h2>
            <ul className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
              {edicion.sedes.map((s) => (
                <li
                  key={s.documentId ?? s.id}
                  className="overflow-hidden rounded-2xl border border-gray-300 bg-white transition-colors hover:border-brand-orange"
                >
                  {s.imagenPortada?.url && (
                    <img
                      src={mediaUrl(s.imagenPortada.url)}
                      alt={s.imagenPortada.alternativeText ?? s.ciudad}
                      className="aspect-[4/3] w-full object-cover"
                      loading="lazy"
                    />
                  )}
                  <div className="p-6">
                    <h3 className="font-display text-xl font-semibold">{s.ciudad}</h3>
                    {s.nombreLugar && (
                      <p className="mt-1 text-sm font-medium text-brand-orange-deep">
                        {s.nombreLugar}
                      </p>
                    )}
                    {s.descripcionCorta && (
                      <p className="mt-3 text-sm text-gray-700">{s.descripcionCorta}</p>
                    )}
                    {s.direccion && (
                      <p className="mt-3 text-xs text-gray-600">{s.direccion}</p>
                    )}
                    {s.enlaceMapa && (
                      <div className="mt-4">
                        <SedeMapaLink enlaceMapa={s.enlaceMapa} ciudad={s.ciudad} />
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Equipo de la edición: coordinador + monitores */}
        {(edicion.coordinador || (edicion.monitores && edicion.monitores.length > 0)) && (
          <section className="container-emjc mt-16">
            <h2 className="font-display text-3xl font-semibold md:text-4xl">
              Equipo de esta edición
            </h2>

            {edicion.coordinador && (
              <div className="mt-8">
                <p
                  className={`text-sm font-semibold uppercase tracking-widest ${accentTextDeep[accent]}`}
                >
                  Coordinación
                </p>
                <PersonaCard persona={edicion.coordinador} />
              </div>
            )}

            {edicion.monitores && edicion.monitores.length > 0 && (
              <div className="mt-10">
                <p
                  className={`text-sm font-semibold uppercase tracking-widest ${accentTextDeep[accent]}`}
                >
                  Monitores
                </p>
                <ul className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {edicion.monitores.map((m) => (
                    <li key={m.documentId ?? m.id}>
                      <PersonaCard persona={m} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* Galería asociada (preview) */}
        {edicion.galeria && (
          <section className="container-emjc mt-16">
            <h2 className="font-display text-3xl font-semibold md:text-4xl">
              Galería de la edición
            </h2>
            <p className="mt-3 text-gray-700">
              {edicion.galeria.descripcion ?? edicion.galeria.titulo}
            </p>
            <div className="mt-6">
              <Link
                to={`/galeria/${edicion.galeria.slug}`}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-brand-paper transition hover:opacity-90 ${accentBg[accent]}`}
              >
                Ver fotos →
              </Link>
            </div>
          </section>
        )}

        {/* Noticias relacionadas */}
        {edicion.noticias && edicion.noticias.length > 0 && (
          <section className="container-emjc mt-16">
            <h2 className="font-display text-3xl font-semibold md:text-4xl">
              Noticias relacionadas
            </h2>
            <ul className="mt-6 space-y-3">
              {edicion.noticias.map((n) => (
                <li key={n.documentId ?? n.id}>
                  <Link
                    to={`/noticias/${n.slug}`}
                    className="block rounded-2xl border border-gray-300 bg-white p-5 transition hover:border-brand-orange"
                  >
                    <p className="text-xs uppercase tracking-widest text-gray-700">
                      {new Date(n.fechaPublicacion).toLocaleDateString("es-ES", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                    <p className="mt-1 font-display text-lg font-semibold">{n.titulo}</p>
                    {n.resumen && (
                      <p className="mt-2 text-sm text-gray-700">{n.resumen}</p>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Volver */}
        <div className="container-emjc mt-16">
          <Link
            to={`/programas/${programaSlug}`}
            className="text-sm font-semibold text-brand-ink hover:text-brand-orange-deep hover:underline"
          >
            ← Volver a {nombrePrograma}
          </Link>
        </div>
      </article>
    </>
  );
}

/**
 * Tarjeta de persona con respeto al consentimiento RGPD:
 * si la persona no ha firmado consentimiento de imagen, mostramos
 * un avatar neutro aunque el CMS hubiese servido la foto por error.
 *
 * El backend (controlador de api::persona.persona) DEBE filtrar la foto
 * cuando consentimientoRgpd=false, pero esto es defensa en profundidad:
 * el frontend tampoco la pinta.
 */
function PersonaCard({ persona }: { persona: NonNullable<EdicionPrograma["coordinador"]> }) {
  const fotoUrl =
    persona.consentimientoRgpd && persona.foto?.url
      ? mediaUrl(persona.foto.url)
      : undefined;

  return (
    <div className="flex items-center gap-4 rounded-2xl border border-gray-300 bg-white p-4">
      <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-paper ring-1 ring-gray-300">
        {fotoUrl ? (
          <img
            src={fotoUrl}
            alt={persona.foto?.alternativeText ?? persona.nombre}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <span
            aria-hidden="true"
            className="font-display text-lg font-semibold text-gray-700"
          >
            {persona.nombre
              .split(" ")
              .slice(0, 2)
              .map((p) => p[0])
              .join("")
              .toUpperCase()}
          </span>
        )}
      </div>
      <div className="min-w-0">
        <p className="truncate font-display text-base font-semibold">{persona.nombre}</p>
        {persona.rol && (
          <p className="truncate text-sm text-gray-700">{persona.rol}</p>
        )}
      </div>
    </div>
  );
}
